"use client";

import { type HTMLAttributes, type ReactNode, useEffect, useRef } from "react";
import { cn } from "../../../lib";
import {
	EDITABLE_FIELD_COLUMN_CLASS,
	EDITABLE_FIELD_COLUMNS_SHELL_CLASS,
	EDITABLE_FIELD_FOOTER_BAR_CLASS,
	EDITABLE_FIELD_HEADER_BAR_CLASS,
	EDITABLE_FIELD_HEADER_BG_PLAIN_CLASS,
	EDITABLE_FIELD_HEADER_GRADIENT_OVERLAY_CLASS,
	EDITABLE_FIELD_HEADER_SEPARATOR_CLASS,
	EDITABLE_FIELD_HEADER_TITLE_CLASS,
	EDITABLE_FIELD_HEADER_VARIANT_DEFAULT,
	EDITABLE_FIELD_INPUT_BG_CLASS,
	EDITABLE_FIELD_PANEL_CLASS,
	EDITABLE_FIELD_SCROLL_MARGIN_CLASS,
	EDITABLE_FIELD_SECTION_BG_CLASS,
	type EditableFieldHeaderVariant,
} from "./chrome";
import { EditableFieldProvider, useEditableField } from "./context";
import { scrollEditableFieldIntoView } from "./scroll";

export interface EditableFieldRootProps extends HTMLAttributes<HTMLDivElement> {
	/** Edit mode flag for consumers / data attributes (body stays section-coloured). */
	editing?: boolean;
	/**
	 * When false, skips the bordered panel shell (e.g. a column inside
	 * {@link EditableFieldColumns}).
	 */
	panel?: boolean;
	/** Header surface — defaults to {@link EDITABLE_FIELD_HEADER_VARIANT_DEFAULT}. */
	headerVariant?: EditableFieldHeaderVariant;
	/**
	 * When editing becomes true (pencil), smoothly scroll this field into view
	 * with {@link EDITABLE_FIELD_SCROLL_MARGIN_CLASS} top padding. Default true.
	 */
	scrollOnEdit?: boolean;
	children: ReactNode;
}

/**
 * Root shell for an editable field panel. Compose with Header / Body / Footer.
 *
 * Extensible for TipTap, textarea, inputs, switches, entity-viewer collections, etc.
 */
export function EditableFieldRoot({
	editing = false,
	panel = true,
	headerVariant = EDITABLE_FIELD_HEADER_VARIANT_DEFAULT,
	scrollOnEdit = true,
	className,
	children,
	...props
}: EditableFieldRootProps) {
	const rootRef = useRef<HTMLDivElement>(null);
	const wasEditingRef = useRef(false);

	useEffect(() => {
		if (!scrollOnEdit) {
			return;
		}
		if (!editing) {
			wasEditingRef.current = false;
			return;
		}
		if (wasEditingRef.current) {
			return;
		}
		wasEditingRef.current = true;

		let cancelled = false;
		let raf2 = 0;
		function runScroll() {
			if (!cancelled) {
				scrollEditableFieldIntoView(rootRef.current);
			}
		}

		// Wait for edit chrome (TipTap control shell, textarea mount) + any
		// focus without-scroll to settle before aligning the field.
		const raf1 = requestAnimationFrame(() => {
			raf2 = requestAnimationFrame(runScroll);
		});
		const timeoutId = window.setTimeout(runScroll, 80);

		return () => {
			cancelled = true;
			cancelAnimationFrame(raf1);
			cancelAnimationFrame(raf2);
			window.clearTimeout(timeoutId);
		};
	}, [editing, scrollOnEdit]);

	return (
		<EditableFieldProvider editing={editing} headerVariant={headerVariant}>
			<div
				ref={rootRef}
				className={cn(
					panel && EDITABLE_FIELD_PANEL_CLASS,
					EDITABLE_FIELD_SCROLL_MARGIN_CLASS,
					className,
				)}
				data-editing={editing ? "true" : "false"}
				data-sq-editable-field=""
				{...props}
			>
				{children}
			</div>
		</EditableFieldProvider>
	);
}

export interface EditableFieldHeaderProps {
	title?: ReactNode;
	/** Content after the title (e.g. formatting toolbar). Shown after a separator when set. */
	leading?: ReactNode;
	end?: ReactNode;
	className?: string;
	children?: ReactNode;
	/** Override root / default header surface. */
	variant?: EditableFieldHeaderVariant;
}

/**
 * Opaque tile header — plain solid base, optional 20% gradient overlay.
 */
export function EditableFieldHeader({
	title,
	leading,
	end,
	className,
	children,
	variant,
}: EditableFieldHeaderProps) {
	const { headerVariant } = useEditableField();
	const resolvedVariant = variant ?? headerVariant;
	const showGradient = resolvedVariant === "gradient";

	return (
		<div
			className={cn(
				EDITABLE_FIELD_HEADER_BAR_CLASS,
				"relative isolate overflow-hidden",
				EDITABLE_FIELD_HEADER_BG_PLAIN_CLASS,
				className,
			)}
			data-sq-editable-field-header=""
			data-header-variant={resolvedVariant}
		>
			{showGradient ? (
				<div
					className={EDITABLE_FIELD_HEADER_GRADIENT_OVERLAY_CLASS}
					aria-hidden
				/>
			) : null}
			{children != null ? (
				<div className="relative z-10 flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto">
					{children}
				</div>
			) : (
				<div className="relative z-10 flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto">
					{title != null ? (
						<EditableFieldTitle>{title}</EditableFieldTitle>
					) : null}
					{leading ? (
						<>
							<EditableFieldSeparator />
							{leading}
						</>
					) : null}
				</div>
			)}
			{end ? (
				<div className="relative z-10 ml-2 flex shrink-0 items-center gap-1">
					{end}
				</div>
			) : null}
		</div>
	);
}

export function EditableFieldTitle({
	children,
	className,
}: {
	children: ReactNode;
	className?: string;
}) {
	if (typeof children === "string") {
		return (
			<p className={cn(EDITABLE_FIELD_HEADER_TITLE_CLASS, className)}>
				{children}
			</p>
		);
	}
	return (
		<div className={cn(EDITABLE_FIELD_HEADER_TITLE_CLASS, className)}>
			{children}
		</div>
	);
}

export function EditableFieldSeparator({ className }: { className?: string }) {
	return (
		<div
			className={cn(EDITABLE_FIELD_HEADER_SEPARATOR_CLASS, className)}
			aria-hidden
		/>
	);
}

export interface EditableFieldBodyProps extends HTMLAttributes<HTMLDivElement> {
	/**
	 * Body fill. Defaults to accordion section colour and does **not** flip on
	 * edit — put {@link EDITABLE_FIELD_CONTROL_SURFACE_CLASS} / Input / Textarea
	 * on the control itself.
	 * - `section` (default): accordion-body colour
	 * - `input`: card/input fill (rare; prefer styling the control)
	 */
	surface?: "section" | "input";
	children: ReactNode;
}

/**
 * Content area — stays section-coloured while editing so only the control
 * reads as the input surface.
 */
export function EditableFieldBody({
	surface = "section",
	className,
	children,
	...props
}: EditableFieldBodyProps) {
	const { editing } = useEditableField();
	const bg =
		surface === "input"
			? EDITABLE_FIELD_INPUT_BG_CLASS
			: EDITABLE_FIELD_SECTION_BG_CLASS;

	return (
		<div
			className={cn("min-h-min", bg, className)}
			data-sq-editable-field-body=""
			data-editing={editing ? "true" : "false"}
			{...props}
		>
			{children}
		</div>
	);
}

export interface EditableFieldFooterProps extends HTMLAttributes<HTMLElement> {
	children: ReactNode;
	as?: "div" | "nav" | "footer";
}

/**
 * Bottom bar — always accordion section colour (does not flip when editing).
 */
export function EditableFieldFooter({
	as: Comp = "div",
	className,
	children,
	...props
}: EditableFieldFooterProps) {
	return (
		<Comp
			className={cn(
				EDITABLE_FIELD_FOOTER_BAR_CLASS,
				EDITABLE_FIELD_SECTION_BG_CLASS,
				className,
			)}
			data-sq-editable-field-footer=""
			{...props}
		>
			{children}
		</Comp>
	);
}

export interface EditableFieldColumnsProps
	extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
}

/**
 * Multi-column layout inside one panel (e.g. description | objectives).
 * Row height follows the tallest column’s content; shorter columns stretch.
 * Children should use `panel={false}` and {@link EDITABLE_FIELD_COLUMN_CLASS}.
 */
export function EditableFieldColumns({
	className,
	children,
	...props
}: EditableFieldColumnsProps) {
	return (
		<div
			className={cn(
				EDITABLE_FIELD_COLUMNS_SHELL_CLASS,
				"grid grid-cols-1 items-stretch justify-items-stretch auto-rows-auto md:grid-cols-2 md:divide-x md:divide-border",
				className,
			)}
			data-sq-editable-field-columns=""
			{...props}
		>
			{children}
		</div>
	);
}

/** Column root helper — full-height flex column inside {@link EditableFieldColumns}. */
export function EditableFieldColumn({
	className,
	children,
	...props
}: HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn(EDITABLE_FIELD_COLUMN_CLASS, className)}
			data-sq-editable-field-column=""
			{...props}
		>
			{children}
		</div>
	);
}
